import {
  CheckCircleOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Image, Page, Text, View } from '@react-pdf/renderer'
import {
  Button,
  Col,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  DRA_SUB_MAPPINGS,
  GeckoDRA,
  GeckoSubDRA,
  RautReportIndication,
} from 'features/indicationTriage'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PageSubheader } from 'features/reporting/pdfGeneration/components/shared/PageSubHeader'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

import {
  displayMean,
  displayMin,
  displayWallLoss,
} from './subSections/indicationDetailsComponents/indicationDisplay/utils/valueDisplayHelpers'
import { RenderedVisualization } from './subSections/indicationDetailsComponents/LoadSavedIndicationGraphs'
import { IndicationSummaryPage } from './subSections/indications/IndicationSummaryPage'
const AntText = Typography.Text

// =============================================================================
// STYLED COMPONENTS - Two Panel Layout
// =============================================================================

const TwoPanelContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  padding: 1.5rem;
  background: ${({ theme }) =>
    theme.forecast.pageBackground1 || theme.forecast.pageBackground};
  min-height: 100%;
  height: calc(100vh - 200px);

  @media (max-width: 1200px) {
    flex-direction: column;
    height: auto;
  }
`

const LeftPanel = styled.div`
  flex: 0 0 22rem;
  min-width: 20rem;
  max-width: 24rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;

  @media (max-width: 1200px) {
    flex: none;
    max-width: 100%;
    max-height: 24rem;
  }
`

const RightPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
`

const PanelCard = styled.div`
  border-radius: 0.5rem;
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
  background: ${({ theme }) =>
    theme.forecast.cardBackground || theme.forecast.background1};
  box-shadow: 0 0.0625rem 0.25rem
    ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.04)'};
  overflow: hidden;
`

const PanelHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || 'transparent'};
`

const PanelTitle = styled(AntText)`
  && {
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => theme.forecast.activeText};
    letter-spacing: -0.01em;
  }
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;

  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) =>
      theme.forecast.cardGroupBackground || 'transparent'};
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const DetailScrollableContent = styled(ScrollableContent)`
  padding: 1.25rem;
`

// =============================================================================
// STYLED COMPONENTS - Indication List
// =============================================================================

const ComponentGroup = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const ComponentGroupHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || 'transparent'};
  cursor: pointer;
  transition: background-color 0.15s ease;
  text-align: left;

  &:hover {
    background: ${({ theme }) =>
      theme.forecast.hoverGhostButton || 'rgba(0, 0, 0, 0.03)'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary || '#007BFF'};
    outline-offset: 2px;
  }
`

const ComponentGroupIcon = styled.span<{ $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
  transition: transform 0.2s ease;
  transform: ${({ $isExpanded }) =>
    $isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
`

const ComponentGroupTitle = styled(AntText)`
  && {
    flex: 1;
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.forecast.activeText};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const ComponentGroupContent = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'block' : 'none')};
  padding-left: 0.5rem;
  padding-top: 0.375rem;
`

const IndicationListItem = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.625rem 0.75rem;
  margin-bottom: 0.25rem;
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected
        ? theme.forecast.primary || '#007BFF'
        : theme.forecast.cardDividerBackground || theme.forecast.border};
  border-radius: 0.375rem;
  background: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.forecast.blueGlassBackground || 'rgba(0, 100, 255, 0.08)'
      : theme.forecast.cardBackground || theme.forecast.background1};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary || '#007BFF'};
    box-shadow: 0 0.0625rem 0.25rem
      ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary || '#007BFF'};
    outline-offset: 2px;
  }
`

const IndicationItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
`

const IndicationItemTitle = styled(AntText)`
  && {
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${({ theme }) => theme.forecast.activeText};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }
`

const VisualizationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  white-space: nowrap;
  background: ${({ theme }) =>
    theme.forecast.greenGlassBackground || 'rgba(82, 196, 26, 0.1)'};
  color: #52c41a;
`

const IndicationItemStats = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const StatItem = styled.span`
  white-space: nowrap;
`

// =============================================================================
// STYLED COMPONENTS - Detail Panel
// =============================================================================

const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`

const DetailTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const DetailTitle = styled(AntText)`
  && {
    font-size: 1.125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.forecast.activeText};
    letter-spacing: -0.01em;
  }
`

const SelectionInfoSection = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
`

const SelectionInfoTitle = styled(Typography.Title)`
  && {
    font-size: 0.875rem;
    font-weight: 600;
    color: ${({ theme }) => theme.forecast.activeText};
    margin-bottom: 0.75rem;
  }
`

const SelectionLabel = styled(Typography.Text)`
  && {
    font-weight: 500;
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const SelectionValue = styled(Typography.Text)`
  && {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const EmptyDetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem 2rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const EmptyDetailText = styled(AntText)`
  && {
    font-size: 0.9375rem;
    color: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const EmptyDetailSubtext = styled(AntText)`
  && {
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.forecast.tertiaryText};
  }
`

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function indicationPrecisionizer(
  indication: RautReportIndication,
  geometricUnit: GeckoMeasurementUnit,
  measurementPrecision: number
): RautReportIndication {
  return {
    ...indication,
    customInfo: {
      ...indication.customInfo,
      stats: {
        ...indication.customInfo.stats,
        minimum: indication.customInfo.stats.minimum
          .convertTo(geometricUnit)
          .round(measurementPrecision),
        maximum: indication.customInfo.stats.maximum
          .convertTo(geometricUnit)
          .round(measurementPrecision),
        mean: indication.customInfo.stats.mean
          .convertTo(geometricUnit)
          .round(measurementPrecision),
      },
    },
  }
}

// =============================================================================
// MEMOIZED LIST ITEM COMPONENT
// =============================================================================

interface MemoizedIndicationListItemProps {
  indication: RautReportIndication
  isSelected: boolean
  hasVisualization: boolean
  geometricUnit: GeckoMeasurementUnit
  measurementPrecision: number
  nominal?: GeckoMeasurement | GeckoMeasurementRange
  onSelect: (id: string) => void
}

const IndicationListItemInner: FC<MemoizedIndicationListItemProps> = ({
  indication,
  isSelected,
  hasVisualization,
  geometricUnit,
  measurementPrecision,
  nominal,
  onSelect,
}) => {
  const precisionizedIndication = useMemo(
    () =>
      indicationPrecisionizer(indication, geometricUnit, measurementPrecision),
    [indication, geometricUnit, measurementPrecision]
  )

  const handleClick = useCallback(() => {
    onSelect(indication.id)
  }, [indication.id, onSelect])

  return (
    <IndicationListItem
      $isSelected={isSelected}
      onClick={handleClick}
      aria-pressed={isSelected}
      aria-label={`Indication ${indication.title}${hasVisualization ? ', has visualization' : ''}`}>
      <IndicationItemHeader>
        <IndicationItemTitle>{indication.title}</IndicationItemTitle>
        {hasVisualization && (
          <VisualizationBadge>
            <CheckCircleOutlined />
          </VisualizationBadge>
        )}
      </IndicationItemHeader>
      <IndicationItemStats>
        <StatItem>
          Min:{' '}
          {displayMin(
            precisionizedIndication.customInfo.stats.minimum,
            geometricUnit,
            measurementPrecision
          )}
        </StatItem>
        {nominal && (
          <StatItem>
            WL:{' '}
            {displayWallLoss(
              precisionizedIndication.customInfo.stats.minimum,
              nominal,
              geometricUnit,
              measurementPrecision
            )}
          </StatItem>
        )}
        <StatItem>DRA: {indication.customInfo.dra_rating}</StatItem>
      </IndicationItemStats>
    </IndicationListItem>
  )
}

const MemoizedIndicationListItem = React.memo(IndicationListItemInner)

// =============================================================================
// MAIN RENDER REACT COMPONENT
// =============================================================================

const RenderReact: FC = () => {
  const {
    indicationsByInspectionId,
    flatIndications,
    isLoading,
    inspections,
    componentsByInspection,
    nominalsByInspection,
    renderedImageUrls,
  } = useRautReportOps()

  const { getGeometricUnit, measurementPrecision, getDistanceUnit } =
    useUnitSystem()

  const geometricUnit = getGeometricUnit()
  const distanceUnit = getDistanceUnit()

  // State for selected indication
  const [selectedIndicationId, setSelectedIndicationId] = useState<
    string | null
  >(null)

  // State for expanded component groups
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )

  // Initialize expanded groups on mount or when inspections change
  useEffect(() => {
    if (inspections.length > 0) {
      setExpandedGroups((prev) => {
        // Only initialize if not already set
        if (Object.keys(prev).length === 0) {
          const initialExpanded: Record<string, boolean> = {}
          inspections.forEach((inspection) => {
            initialExpanded[inspection.id] = true
          })
          return initialExpanded
        }
        return prev
      })
    }
  }, [inspections])

  // Select first indication when data loads
  useEffect(() => {
    if (flatIndications.length > 0 && !selectedIndicationId) {
      setSelectedIndicationId(flatIndications[0].id)
    }
  }, [flatIndications, selectedIndicationId])

  const toggleGroup = useCallback((inspectionId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [inspectionId]: !prev[inspectionId],
    }))
  }, [])

  const handleSelectIndication = useCallback((indicationId: string) => {
    setSelectedIndicationId(indicationId)
  }, [])

  // Create a flat lookup map for O(1) indication lookups
  const indicationLookupMap = useMemo(() => {
    const map = new Map<
      string,
      {
        indication: RautReportIndication
        inspectionId: string
      }
    >()

    for (const inspection of inspections) {
      const indications = indicationsByInspectionId.get(inspection.id) || []
      for (const indication of indications) {
        map.set(indication.id, {
          indication,
          inspectionId: inspection.id,
        })
      }
    }

    return map
  }, [inspections, indicationsByInspectionId])

  // Find the selected indication and its related data using O(1) lookup
  const selectedIndication = useMemo(() => {
    if (!selectedIndicationId) return null

    const lookupResult = indicationLookupMap.get(selectedIndicationId)
    if (!lookupResult) return null

    const { indication, inspectionId } = lookupResult

    return {
      indication: indicationPrecisionizer(
        indication,
        geometricUnit,
        measurementPrecision
      ),
      inspectionId,
      nominal: nominalsByInspection.get(inspectionId),
    }
  }, [
    selectedIndicationId,
    indicationLookupMap,
    nominalsByInspection,
    geometricUnit,
    measurementPrecision,
  ])

  if (isLoading) {
    return (
      <TwoPanelContainer>
        <PanelCard style={{ flex: 1, display: 'flex' }}>
          <LoadingContainer style={{ flex: 1 }}>
            <Spin size='large' />
            <AntText>Loading indications...</AntText>
          </LoadingContainer>
        </PanelCard>
      </TwoPanelContainer>
    )
  }

  if (!flatIndications?.length) {
    return (
      <TwoPanelContainer>
        <PanelCard style={{ flex: 1, display: 'flex' }}>
          <EmptyDetailPanel style={{ flex: 1 }}>
            <EmptyDetailText>
              No indications found for this report.
            </EmptyDetailText>
          </EmptyDetailPanel>
        </PanelCard>
      </TwoPanelContainer>
    )
  }

  return (
    <TwoPanelContainer>
      {/* LEFT PANEL - Indication List */}
      <LeftPanel>
        <PanelCard
          style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <PanelHeader>
            <PanelTitle>Indications</PanelTitle>
          </PanelHeader>
          <ScrollableContent>
            {inspections.map((inspection) => {
              const inspectionIndications =
                indicationsByInspectionId.get(inspection.id) || []
              const component = componentsByInspection.get(inspection.id)
              const componentName = component?.data.name || 'Unknown Component'
              const isExpanded = expandedGroups[inspection.id] ?? true

              if (inspectionIndications.length === 0) {
                return null
              }

              return (
                <ComponentGroup key={inspection.id}>
                  <ComponentGroupHeader
                    $isExpanded={isExpanded}
                    onClick={() => toggleGroup(inspection.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`group-${inspection.id}`}>
                    <ComponentGroupIcon $isExpanded={isExpanded}>
                      {isExpanded ? <DownOutlined /> : <RightOutlined />}
                    </ComponentGroupIcon>
                    <ComponentGroupTitle>{componentName}</ComponentGroupTitle>
                  </ComponentGroupHeader>
                  <ComponentGroupContent
                    $isExpanded={isExpanded}
                    id={`group-${inspection.id}`}>
                    {inspectionIndications.map((indication) => (
                      <MemoizedIndicationListItem
                        key={indication.id}
                        indication={indication}
                        isSelected={selectedIndicationId === indication.id}
                        hasVisualization={!!renderedImageUrls[indication.id]}
                        geometricUnit={geometricUnit}
                        measurementPrecision={measurementPrecision}
                        nominal={nominalsByInspection.get(inspection.id)}
                        onSelect={handleSelectIndication}
                      />
                    ))}
                  </ComponentGroupContent>
                </ComponentGroup>
              )
            })}
          </ScrollableContent>
        </PanelCard>
      </LeftPanel>

      {/* RIGHT PANEL - Detail View */}
      <RightPanel>
        <PanelCard
          style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {selectedIndication ? (
            <IndicationDetailView
              indication={selectedIndication.indication}
              geometricUnit={geometricUnit}
              distanceUnit={distanceUnit}
              nominal={selectedIndication.nominal}
              measurementPrecision={measurementPrecision}
              imageUrl={renderedImageUrls[selectedIndication.indication.id]}
            />
          ) : (
            <EmptyDetailPanel>
              <EmptyDetailText>No indication selected</EmptyDetailText>
              <EmptyDetailSubtext>
                Select an indication from the list to view its details
              </EmptyDetailSubtext>
            </EmptyDetailPanel>
          )}
        </PanelCard>
      </RightPanel>
    </TwoPanelContainer>
  )
}

// =============================================================================
// INDICATION DETAIL VIEW COMPONENT
// =============================================================================

interface IndicationDetailViewProps {
  indication: RautReportIndication
  geometricUnit: GeckoMeasurementUnit
  distanceUnit: GeckoMeasurementUnit
  nominal?: GeckoMeasurement | GeckoMeasurementRange
  measurementPrecision: number
  imageUrl?: string
}

const IndicationDetailViewInner: FC<IndicationDetailViewProps> = ({
  indication,
  geometricUnit,
  distanceUnit,
  nominal,
  measurementPrecision,
  imageUrl,
}) => {
  const { rolodexPatch } = useRolodexEdit()

  // State for DRA rating editing
  const [draRating, setDRARating] = useState<GeckoDRA>(
    indication.customInfo.dra_rating
  )
  const [subRating, setSubRating] = useState<GeckoSubDRA>(
    indication.customInfo.sub_rating
  )
  const [isSaving, setIsSaving] = useState(false)

  // Update DRA state when indication changes
  useEffect(() => {
    setDRARating(indication.customInfo.dra_rating)
    setSubRating(indication.customInfo.sub_rating)
  }, [
    indication.id,
    indication.customInfo.dra_rating,
    indication.customInfo.sub_rating,
  ])

  // Auto-select first sub-rating when DRA changes
  useEffect(() => {
    const validSubRatings = DRA_SUB_MAPPINGS[draRating]
    if (!validSubRatings.includes(subRating)) {
      setSubRating(validSubRatings[0])
    }
  }, [draRating, subRating])

  // DRA options for the select dropdown
  const draOptions = useMemo(
    () =>
      Object.values(GeckoDRA).map((dra) => ({
        value: dra,
        label: dra,
      })),
    []
  )

  // Sub-rating options based on selected DRA
  const subOptions = useMemo(
    () =>
      DRA_SUB_MAPPINGS[draRating].map((sub) => ({
        value: sub,
        label: sub,
      })),
    [draRating]
  )

  // Check if ratings have changed from the original
  const hasChanges =
    draRating !== indication.customInfo.dra_rating ||
    subRating !== indication.customInfo.sub_rating

  // Save handler to persist DRA changes to the indication entry
  const handleSaveDRA = async () => {
    try {
      setIsSaving(true)
      await rolodexPatch([
        {
          id: indication.id,
          data: {
            custom_info: {
              ...indication.customInfo,
              dra_rating: draRating,
              sub_rating: subRating,
              stats: {
                minimum: indication.customInfo.stats.minimum.toNumber(
                  GeckoMeasurementUnit.Meters
                ),
                mean: indication.customInfo.stats.mean.toNumber(
                  GeckoMeasurementUnit.Meters
                ),
                maximum: indication.customInfo.stats.maximum.toNumber(
                  GeckoMeasurementUnit.Meters
                ),
                std_dev: indication.customInfo.stats.std_dev.toNumber(
                  GeckoMeasurementUnit.Meters
                ),
                min_coords: indication.customInfo.stats.min_coords,
                max_coords: indication.customInfo.stats.max_coords,
              },
            },
          },
        },
      ])
      message.success('DRA rating saved successfully')
    } catch (error) {
      console.error('Error saving DRA rating:', error)
      message.error('Failed to save DRA rating')
    } finally {
      setIsSaving(false)
    }
  }

  // Component to display selection information
  const SelectionInfo = () => {
    if (!indication.selection) return null

    const selection = indication.selection

    const xStart = selection.x.min
    const yStart = selection.y.min
    const xEnd = selection.x.max
    const yEnd = selection.y.max

    return (
      <SelectionInfoSection>
        <SelectionInfoTitle level={5}>Selection Coordinates</SelectionInfoTitle>
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <SelectionLabel>X-Range: </SelectionLabel>
            <SelectionValue>
              {xStart.toNumber(distanceUnit).toFixed(4)} -{' '}
              {xEnd.toNumber(distanceUnit).toFixed(4)} {distanceUnit.id}
            </SelectionValue>
          </Col>
          <Col span={12}>
            <SelectionLabel>Width: </SelectionLabel>
            <SelectionValue>
              {xEnd.minus(xStart).toNumber(distanceUnit).toFixed(4)}{' '}
              {distanceUnit.id}
            </SelectionValue>
          </Col>
          <Col span={12}>
            <SelectionLabel>Y-Range: </SelectionLabel>
            <SelectionValue>
              {yStart.toNumber(distanceUnit).toFixed(4)} -{' '}
              {yEnd.toNumber(distanceUnit).toFixed(4)} {distanceUnit.id}
            </SelectionValue>
          </Col>
          <Col span={12}>
            <SelectionLabel>Height: </SelectionLabel>
            <SelectionValue>
              {yEnd.minus(yStart).toNumber(distanceUnit).toFixed(4)}{' '}
              {distanceUnit.id}
            </SelectionValue>
          </Col>
        </Row>
      </SelectionInfoSection>
    )
  }

  // Display stats section
  const StatsSection = () => {
    const stats = indication.customInfo.stats

    return (
      <SelectionInfoSection>
        <SelectionInfoTitle level={5}>Thickness Statistics</SelectionInfoTitle>
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <SelectionLabel>Minimum: </SelectionLabel>
            <SelectionValue>
              {displayMin(stats.minimum, geometricUnit, measurementPrecision)}
            </SelectionValue>
          </Col>
          <Col span={12}>
            <SelectionLabel>Mean: </SelectionLabel>
            <SelectionValue>
              {displayMean(stats.mean, geometricUnit, measurementPrecision)}
            </SelectionValue>
          </Col>
          <Col span={12}>
            <SelectionLabel>Wall Loss: </SelectionLabel>
            <SelectionValue>
              {nominal
                ? displayWallLoss(
                    stats.minimum,
                    nominal,
                    geometricUnit,
                    measurementPrecision
                  )
                : 'N/A'}
            </SelectionValue>
          </Col>
          <Col span={24}>
            <SelectionLabel>DRA Rating: </SelectionLabel>
            <Space style={{ marginTop: 8, display: 'flex' }}>
              <Select
                options={draOptions}
                value={draRating}
                onChange={setDRARating}
                style={{ minWidth: 120 }}
              />
              <Select
                options={subOptions}
                value={subRating}
                onChange={setSubRating}
                style={{ minWidth: 100 }}
              />
              {hasChanges && (
                <Button
                  type='primary'
                  size='small'
                  loading={isSaving}
                  onClick={handleSaveDRA}>
                  Save
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </SelectionInfoSection>
    )
  }

  return (
    <>
      <PanelHeader>
        <DetailHeader>
          <DetailTitleSection>
            <DetailTitle>Indication: {indication.title}</DetailTitle>
          </DetailTitleSection>
        </DetailHeader>
      </PanelHeader>

      <DetailScrollableContent>
        {/* Show the rendered visualization (from new pipeline or legacy saved) */}
        <RenderedVisualization
          indicationId={indication.id}
          imageUrl={imageUrl}
        />

        <StatsSection />
        <SelectionInfo />
      </DetailScrollableContent>
    </>
  )
}

const IndicationDetailView = React.memo(IndicationDetailViewInner)

// =============================================================================
// PDF RENDER COMPONENT (UNCHANGED)
// =============================================================================

const RenderPDF: FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    indicationsWithImages,
    renderedImageUrls,
    indicationsByInspectionId,
    nominalsByInspection,
    inspections,
    componentsByInspection,
  } = useRautReportOps()

  // TODO: Make the title handling better.
  const indicationTitleHandler = useCallback(
    (item: string) =>
      formData.mainInfo.scanIndicationMode ? item.split('_')[0] : item,
    [formData.mainInfo.scanIndicationMode]
  )

  const { getGeometricUnit, getDistanceUnit, measurementPrecision } =
    useUnitSystem()

  const geometricUnit = getGeometricUnit()
  const distanceUnit = getDistanceUnit()

  // Create a map from indication ID to its inspection's nominal thickness
  const indicationToNominalMap = useMemo(() => {
    const map = new Map<string, GeckoMeasurement | GeckoMeasurementRange>()

    indicationsByInspectionId.forEach((indications, inspectionId) => {
      const nominal = nominalsByInspection.get(inspectionId)
      if (nominal) {
        indications.forEach((indication) => {
          map.set(indication.id, nominal)
        })
      }
    })

    return map
  }, [indicationsByInspectionId, nominalsByInspection])

  const precisionifiedIndications = useMemo(() => {
    const withImagesIds = new Set(indicationsWithImages.map((i) => i.id))
    const sorted: RautReportIndication[] = []
    for (const inspection of inspections) {
      const group = indicationsByInspectionId.get(inspection.id) ?? []
      sorted.push(...group.filter((ind) => withImagesIds.has(ind.id)))
    }
    return sorted.map((indication) =>
      indicationPrecisionizer(indication, geometricUnit, measurementPrecision)
    )
  }, [
    geometricUnit,
    indicationsWithImages,
    measurementPrecision,
    inspections,
    indicationsByInspectionId,
  ])

  const indicationToComponentMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const inspection of inspections) {
      const component = componentsByInspection.get(inspection.id)
      if (component) {
        const group = indicationsByInspectionId.get(inspection.id) ?? []
        for (const indication of group) {
          map.set(indication.id, component.data.name)
        }
      }
    }
    return map
  }, [inspections, componentsByInspection, indicationsByInspectionId])

  const hasMultipleComponents = componentsByInspection.size > 1
  const hasRegisteredFirstPage = useRef(false)

  // Reset the page registration flag when indications change
  useEffect(() => {
    hasRegisteredFirstPage.current = false
  }, [precisionifiedIndications])

  const formatValue = (value?: number): string => {
    if (value === undefined) return 'N/A'
    return value.toFixed(3)
  }

  // If no indications with images, show a message
  if (precisionifiedIndications.length === 0) {
    return (
      <Page size='A4' style={PDFStyles.page} id='indications'>
        <View id='scan-details' />
        <Text
          render={({ pageNumber }) => {
            onRender?.({
              id: 'indications',
              pageNumber,
            })
            onRender?.({
              id: 'scan-details',
              pageNumber,
            })
            return ''
          }}
        />

        <PageHeader
          title={
            formData.mainInfo.scanIndicationMode
              ? 'Scan Details'
              : 'Indication Details'
          }
        />

        <View style={PDFStyles.content}>
          <Text style={PDFStyles.noImageMessage}>
            No indication visualizations available
          </Text>
        </View>

        <PageFooter
          jobNumber={formData.coverPageData.geckoJobId}
          revisionNumber={formData.revisionMetadata.revisionNumber}
          prelim={
            !(
              formData.signoff?.reviewerSignoff &&
              formData.signoff.supervisorSignoff
            )
          }
        />
      </Page>
    )
  }

  // Otherwise, render one indication per page
  return (
    <>
      <IndicationSummaryPage
        indications={precisionifiedIndications}
        savedIndicationGraphs={formData.savedIndicationGraphs ?? {}}
        scanIndicationMode={formData.mainInfo.scanIndicationMode}
        indicationToNominalMap={indicationToNominalMap}
        measurementPrecision={measurementPrecision}
        onRender={onRender}
        jobNumber={formData.coverPageData.geckoJobId}
        revisionNumber={formData.revisionMetadata.revisionNumber}
        distanceUnit={distanceUnit}
        geometricUnit={geometricUnit}
        reviewerSignedOff={!!formData.signoff?.reviewerSignoff}
        supervisorSignedOff={!!formData.signoff?.supervisorSignoff}
        showComponentColumn={hasMultipleComponents}
        indicationToComponentMap={indicationToComponentMap}
      />
      <>
        {precisionifiedIndications.map((indication, index) => {
          const measurementUnit = indication.selection?.x.unit.name || 'mm'
          const imageUrl = renderedImageUrls[indication.id]

          const nominal = indicationToNominalMap.get(indication.id)

          return (
            <Page
              key={indication.id}
              size='A4'
              orientation='landscape'
              style={PDFStyles.page}
              id={indication.id}
              wrap={false}>
              <View id={indication.id}></View>
              {index === 0 && <View id='scan-details' />}
              {/* Hidden text for page tracking - only register the first page */}
              <Text
                render={({ pageNumber }) => {
                  if (!hasRegisteredFirstPage.current && index === 0) {
                    onRender?.({
                      id: 'scan-details',
                      pageNumber,
                    })
                    hasRegisteredFirstPage.current = true
                  }
                  return ''
                }}
              />

              <PageHeader
                title={
                  formData.mainInfo.scanIndicationMode
                    ? 'Scan Details'
                    : 'Indication Details'
                }
              />

              <View style={PDFStyles.content}>
                <View style={PDFStyles.contentContainer}>
                  <View style={{ alignItems: 'flex-start' }}>
                    <PageSubheader
                      title={`${formData.mainInfo.scanIndicationMode ? 'Scan' : 'Indication'}: ${indicationTitleHandler(indication.title)}`}
                      align='left'
                      color='black'
                    />
                  </View>

                  <View style={PDFStyles.contentLayout}>
                    {/* Image container */}
                    <View style={PDFStyles.imageContainer}>
                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          style={PDFStyles.image}
                          cache={false}
                        />
                      ) : (
                        <View style={PDFStyles.graphPlaceholder}>
                          <Text style={PDFStyles.noImageMessage}>
                            No visualization available for this indication
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Stats container */}
                    <View style={PDFStyles.statsContainer}>
                      <Text style={[PDFStyles.sectionLabel, { marginTop: 0 }]}>
                        Thickness Statistics
                      </Text>

                      {/* General stats - Always in meters, converted to mm for display */}
                      <View style={PDFStyles.statRow}>
                        <Text style={PDFStyles.statLabel}>Mean:</Text>
                        <Text style={PDFStyles.statValue}>
                          {indication.customInfo.stats?.mean &&
                            displayMean(
                              indication.customInfo.stats.mean,
                              geometricUnit,
                              measurementPrecision
                            )}{' '}
                        </Text>
                      </View>
                      <View style={PDFStyles.statRow}>
                        <Text style={PDFStyles.statLabel}>Minimum:</Text>
                        <Text style={PDFStyles.statValue}>
                          {indication.customInfo.stats?.minimum &&
                            displayMin(
                              indication.customInfo.stats.minimum,
                              geometricUnit,
                              measurementPrecision
                            )}{' '}
                        </Text>
                      </View>
                      <View style={PDFStyles.statRow}>
                        <Text style={PDFStyles.statLabel}>Wall Loss:</Text>
                        <Text style={PDFStyles.statValue}>
                          {indication.customInfo.stats?.minimum &&
                            displayWallLoss(
                              indication.customInfo.stats.minimum,
                              nominal ?? GeckoMeasurement.Inf(),
                              geometricUnit,
                              measurementPrecision
                            )}
                        </Text>
                      </View>

                      {/* Selection bounds */}
                      {indication.selection && (
                        <>
                          <Text style={PDFStyles.sectionLabel}>
                            Selection Dimensions
                          </Text>
                          <View style={PDFStyles.statRow}>
                            <Text style={PDFStyles.statLabel}>Width:</Text>
                            <Text style={PDFStyles.statValue}>
                              {formatValue(
                                indication.selection.x.max.minus(
                                  indication.selection.x.min
                                ).num
                              )}{' '}
                              {measurementUnit}
                            </Text>
                          </View>
                          <View style={PDFStyles.statRow}>
                            <Text style={PDFStyles.statLabel}>Height:</Text>
                            <Text style={PDFStyles.statValue}>
                              {formatValue(
                                indication.selection.y.max.minus(
                                  indication.selection.y.min
                                ).num
                              )}{' '}
                              {measurementUnit}
                            </Text>
                          </View>

                          <Text style={PDFStyles.sectionLabel}>
                            Selection Bounds
                          </Text>
                          <View style={PDFStyles.statRow}>
                            <Text style={PDFStyles.statLabel}>X-Range:</Text>
                            <Text style={PDFStyles.statValue}>
                              {formatValue(indication.selection.x.min.num)} -{' '}
                              {formatValue(indication.selection.x.max.num)}{' '}
                              {measurementUnit}
                            </Text>
                          </View>
                          <View style={PDFStyles.statRow}>
                            <Text style={PDFStyles.statLabel}>Y-Range:</Text>
                            <Text style={PDFStyles.statValue}>
                              {formatValue(indication.selection.y.min.num)} -{' '}
                              {formatValue(indication.selection.y.max.num)}{' '}
                              {measurementUnit}
                            </Text>
                          </View>
                        </>
                      )}
                    </View>
                  </View>
                </View>
              </View>
              <PageFooter
                jobNumber={formData.coverPageData.geckoJobId}
                revisionNumber={formData.revisionMetadata.revisionNumber}
                prelim={
                  !(
                    formData.signoff?.reviewerSignoff &&
                    formData.signoff.supervisorSignoff
                  )
                }
              />
            </Page>
          )
        })}
      </>
    </>
  )
}

export const IndicationDetails: ReportSection = {
  id: 'indications',
  RenderPDF: RenderPDF,
  RenderReact: RenderReact,
  title: 'Indications',
  description: 'Manage indication screenshots',
  required: true,
  requiredFields: [],
}
